import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Edit3, RefreshCw, X, Loader2, Image as ImageIcon, UploadCloud } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Select } from './shared';
import { useToast } from '../../hooks/use-toast';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CATS = ['crm', 'client', 'award', 'certification'];
const BLANK = { name: '', category: 'crm', img: '', text: '', href: '', order: 0 };

export default function AdminLogos() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/api/admin/logos')).data || []); }
    catch (err) { toast({ title: 'Failed', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.name?.trim()) return toast({ title: 'Name required' });
    setSaving(true);
    try {
      const body = {
        name: editing.name.trim(),
        category: editing.category || 'crm',
        img: editing.img || '', text: editing.text || '', href: editing.href || '',
        order: Number(editing.order) || 0,
      };
      if (editing.id) await api.patch(`/api/admin/logos/${editing.id}`, body);
      else await api.post('/api/admin/logos', body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this logo?')) return;
    try { await api.delete(`/api/admin/logos/${id}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('folder', 'logos');
      const res = await api.post('/api/admin/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setEditing((e) => ({ ...e, img: BACKEND + res.data.url }));
      toast({ title: 'Uploaded' });
    } catch (err) { toast({ title: 'Upload failed', description: formatApiError(err) }); }
    finally { setUploading(false); }
  };

  const grouped = items.reduce((acc, l) => { (acc[l.category] ||= []).push(l); return acc; }, {});

  return (
    <>
      <PageHeader
        title="Logos & Badges"
        subtitle="CRM partners, clients, awards, certifications"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK })}><Plus size={14} /> Add logo</Btn>
          </div>
        }
      />

      {loading ? <Loading /> : Object.keys(grouped).length === 0 ? (
        <AdminCard><Empty /></AdminCard>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, list]) => (
            <AdminCard key={cat} title={cat.toUpperCase()} subtitle={`${list.length} item${list.length > 1 ? 's' : ''}`}>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                {list.map((l) => (
                  <div key={l.id} className="bg-white/5 border border-white/10 rounded-xl p-3 flex flex-col">
                    <div className="h-20 rounded-lg bg-white flex items-center justify-center overflow-hidden">
                      {l.img ? <img src={l.img} alt={l.name} className="max-h-full max-w-full object-contain p-2" /> : <span className="text-slate-600 font-bold text-sm">{l.text || l.name}</span>}
                    </div>
                    <p className="text-xs font-semibold text-white mt-2 truncate">{l.name}</p>
                    <div className="flex gap-1 mt-2">
                      <Btn size="sm" variant="ghost" onClick={() => setEditing(l)}><Edit3 size={12} /></Btn>
                      <Btn size="sm" variant="danger" onClick={() => remove(l.id)}><Trash2 size={12} /></Btn>
                    </div>
                  </div>
                ))}
              </div>
            </AdminCard>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60" />
          <aside className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit logo' : 'New logo'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <Input label="Name *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <Select label="Category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
                <Input label="Order" type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
              </div>
              <div>
                <span className="text-xs font-semibold text-slate-300 mb-1.5 block">Image</span>
                {editing.img && <img src={editing.img} alt="" className="w-full h-24 object-contain bg-white rounded-lg mb-2 p-2" />}
                <div className="flex gap-2">
                  <Input value={editing.img} onChange={(e) => setEditing({ ...editing, img: e.target.value })} placeholder="https://…" className="flex-1" />
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-slate-200 hover:bg-white/10 cursor-pointer">
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <UploadCloud size={14} />}
                    <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files?.[0])} />
                  </label>
                </div>
              </div>
              <Input label="Fallback text" value={editing.text} onChange={(e) => setEditing({ ...editing, text: e.target.value })} placeholder="Shown when image missing" />
              <Input label="Link (optional)" value={editing.href} onChange={(e) => setEditing({ ...editing, href: e.target.value })} placeholder="/crm-integrations" />
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
